<template>
  <!-- cannot put CSS styles here, this element is out of the iframe. use inline styles here -->
  <div
    :style="{
      ...divWrapStyleComputed,
      '--tripla-theme-color': colorsStore.themeColor,
      '--tripla-text-color': colorsStore.textColor
    }"
  >
    <iframe
      v-if="settingsStore.isTermsConditionsEnabled && shouldLoadIframeResource"
      id="terms-conditions-modal"
      :style="termsConditionsIframeStyle"
      @load="loadTermsConditionsModalComponent"
    ></iframe>
    <template v-if="isChatbotActive">
      <iframe
        v-if="shouldLoadIframeResource"
        :id="chatWindowIframeId"
        ref="chatWindowIframe"
        :style="[chatFrameStyle]"
        :class="[{ 'tripla-chat-window--show': isChatOpen }]"
        @load="loadChatWindowComponent"
      />

      <booking-widget-iframe
        v-if="showBWIframe && isChatOpen && hasChatChannel"
        :params="{
          triplabot_sync: true,
          login_token: scriptConfig.dataAttrs['data-triplabot-login-token']
        }"
        style="position: absolute; left: -999em"
      ></booking-widget-iframe>

      <transition name="tripla-scale">
        <div
          v-show="!isChatOpen && !!appStore.session && !isMinimized && !isCustomBotTestMode"
          id="tripla-chat-circle-icon"
          ref="iconElement"
          :style="{
            position: 'fixed',
            bottom: botIconBottom + 'px',
            right: botIconRight + 'px',
            height: iconSize,
            width: iconSize,
            zIndex: Number(triplabotZIndexPosition) - 1
          }"
          :draggable="settingsStore.isChatIconDraggable"
          @dragstart="onChatIconDragStart"
          @dragend="onChatIconDragEnd"
        >
          <icon ref="chatIcon" :toggle-chat="toggleChat" @minimize="minimize" />
        </div>
      </transition>

      <div
        v-if="isMinimized && !isChatOpen && !!appStore.session"
        id="tripla-minimized-chat"
        :style="minimizedChatIconStyle"
        @click="toggleChat"
      >
        <span :style="chatMaximumIconStyle" @click.stop="minimize(false)">
          <expand-icon :style="{ display: 'block', margin: 'auto 4px' }" />
        </span>
        {{ $t('chat_icon.chat') }}
      </div>

      <iframe
        v-if="!isCustomBotTestMode"
        id="tripla-icon-message"
        ref="icon-message-iframe"
        :style="{
          display: 'block',
          border: '0 none',
          bottom: getIconMessageIframeBottomPosition,
          height: getIframeHeight,
          position: 'fixed',
          right: `${getIconMessageIframeRightPositionValue}px`,
          width: getIframeWidth,
          // can't use display none here coz it makes width = 0 and breaks icon message iframe size calculation
          visibility: iconMessageIsShown ? 'visible' : 'hidden',
          zIndex: Number(triplabotZIndexPosition) - 2,
          transition: iconMessageIframeTransitionEnabled ? 'all .3s' : null,
          maxHeight: 'unset !important',
          margin: 'unset !important'
        }"
        @load="loadIconMessageComponent"
        @transitionend="iconMessageIframeTransitionEnabled = false"
      />
      <brr-widget
        v-if="isRenderedBrr"
        :z-index="Number(triplabotZIndexPosition) - 2"
        :bottom-position="iconMessageIframeBottomBrrPosition"
        :right-position="iconMessageIframeRightBrrPosition"
        :left-position="iconMessageIframeLeftBrrPosition"
        :brr-settings="brrSettings"
        :brr-price-list="brrPriceList"
        :target-room-plan="targetRoomPlan"
        :search-params="{ ...priceSearchParams, otaPriceFetching }"
        @close="isBrrWidgetDisplay = !isBrrWidgetDisplay"
        @open-bw="handleOpenBw"
      />
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import Icon from './ChatIcon/index.vue'
import IconMessageWindow from './ChatIcon/IconMessageWindow/index.vue'
import ChatWindow from './ChatWindow/index.vue'
import TermsConditionsModal from './TermsConditionsModal/index.vue'
import { EventBus } from './EventBus'
import {
  localeMapping,
  minimumChatWindowHeight,
  maximumChatWindowHeight,
  defaultIconSize,
  defaultIconPosition,
  htmlTriplaClassName,
  chatWindowIframeId,
  ChatChannels,
  CookieKeys
} from 'javascript/Constants'
import {
  touchHeight,
  touchWidth,
  createAndAppendStyle,
  isSafari,
  getDomainWithoutSubdomain,
  isMobile
} from 'Utilities'
import buildIframePage from 'Utilities/BuildIframePage'
import { setDefaultIconPosition } from 'javascript/Icon'
import { isWebView } from 'Utilities/appAccess'
import { storeSession, removeSession } from 'Utilities/Session'
import Url from 'url-parse'
import BookingWidgetIframe from 'Users/BookingWidgetIframe'
import { User } from 'src/apis'
import BrrWidgetApi from 'src/apis/BrrWidget'
import TemporaryStorageApi from 'src/apis/temporaryStorage'
import Cdp from 'src/apis/Cdp'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import debounce from 'lodash.debounce'
import userSync from 'javascript/helpers/userSync'
import Setting from 'src/apis/Setting'
import ExpandIcon from 'assets/icons/expand.svg?inline'
import { useUserStore } from 'Store/user'
import { useTermsConditionsStore } from 'Store/terms-conditions'
import { useAppStore } from 'Store/app'
import { useBotIconStore } from 'Store/bot-icon'
import { useSettingsStore } from 'Store/settings'
import { mapState } from 'pinia'
import { useColorsStore } from 'Store/colors'
import { useFormStore } from 'Store/form'
import { useIconMessageStore } from 'Store/icon-message'
import { useChatStore } from 'Store/chat'
import { useReservationForm } from 'Store/reservation-form'
import _debounce from 'lodash/debounce'
import Cookies from 'js-cookie'
import BrrWidget from './BRRWidget/index.vue'
import { formatDate } from '../Utilities/TimerHelper'
import { openBookingWidget } from 'Utilities/openBookingWidget'
import findMatchedPage from 'Utilities/findMatchedPage'
import { getChatIconImageFromSettings, delay } from 'Utilities/helpers'

const mobileBrrOffset = 50
const today = new Date()
const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000)
const theDayAfterTomorrow = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000)

const divWrap = {
  position: 'fixed',
  width: '0',
  height: '0',
  bottom: '10px',
  right: '0',
  overflow: 'visible'
}

const divWrapOpenPC = {
  position: 'fixed',
  width: '380px',
  height: 'auto',
  bottom: '10px',
  right: '0',
  overflow: 'visible'
}

const chatFrameStyle = {
  position: 'fixed',
  border: '0 none',
  top: '0',
  left: '0',
  overflow: 'hidden',
  height: '100%',
  width: '100%'
}

export default {
  name: 'ChatBotRootApp',

  components: {
    BookingWidgetIframe,
    Icon,
    ExpandIcon,
    BrrWidget
  },

  inject: ['scriptConfig'],

  setup() {
    return {
      userStore: useUserStore(),
      termsConditionsStore: useTermsConditionsStore(),
      appStore: useAppStore(),
      botIconStore: useBotIconStore(),
      settingsStore: useSettingsStore(),
      colorsStore: useColorsStore(),
      formStore: useFormStore(),
      iconMessageStore: useIconMessageStore(),
      chatStore: useChatStore(),
      reservationFormStore: useReservationForm()
    }
  },

  data() {
    const urlParams = new URL(window.location.href).searchParams
    const checkin = urlParams.get('checkin')
    const checkout = urlParams.get('checkout')
    const adults = urlParams.get('adults')
    return {
      chatWindowIframeId,
      divWrapStyle: divWrap,
      isAnimationCancelled: false,
      iconMessageIframeTransitionEnabled: false,
      chatIconAnimationTimerId: undefined,
      iconFramePositionBottom: this.setIconFrame(
        this.settingsStore.getSettings.mobile_position_bottom,
        this.settingsStore.getSettings.pc_position_bottom,
        defaultIconPosition,
        defaultIconPosition
      ),
      iconFramePositionRight: this.setIconFrame(
        this.settingsStore.getSettings.mobile_position_right,
        this.settingsStore.getSettings.pc_position_right,
        defaultIconPosition,
        defaultIconPosition
      ),
      iconFrameSize: this.setIconFrame(
        this.settingsStore.getSettings.icon_size_mobile,
        this.settingsStore.getSettings.icon_size_pc,
        defaultIconSize,
        defaultIconSize
      ),
      dragPosition: {},
      touchStartPosition: {},
      currentTouch: null,
      showBWIframe: false,
      chatOpenCount: 0,
      isSafari: isSafari(),
      maximizedChat: false,
      isBrrWidgetDisplay: true,
      brrPosition: 'right',
      brrSettings: {},
      brrPriceList: [],
      targetRoomPlan: null,
      priceSearchParams: {},
      newSearchParams: {
        adults: adults || 2,
        checkin: checkin || formatDate(tomorrow),
        checkout: checkout || formatDate(theDayAfterTomorrow),
        is_day_use: false,
        rooms: [],
        is_including_occupied: false,
        type: 'rooms'
      },
      adultsForLinkAndDisplay: 0,
      currentHref: window.location.href,
      otaPriceFetching: false,
      shouldLoadIframeResource:
        urlParams.get('triplabot_open') === 'true' || window.__tripla_guide_website__
    }
  },

  computed: {
    ...mapState(useSettingsStore, {
      isOnStandaloneBookingWidget: 'isOnStandaloneBookingWidget',
      isCustomBotTestMode: 'isCustomBotTestMode',
      chatIconAnimationSettings: 'chatIconAnimationSettings',
      isFacilitySignInEnabled: 'getFacilitySignIn',
      triplabotZIndexPosition: 'triplabotZIndexPosition',
      settings: 'getSettings'
    }),
    ...mapState(useIconMessageStore, {
      getIframeWidth: 'getIframeWidth'
    }),

    isRenderedBrr() {
      if (
        this.newSearchParams.kidsTiers?.filter((tier) => tier.children > 0).length > 0 ||
        this.newSearchParams.rooms?.[0]?.children > 0
      ) {
        return false
      }
      const localeEnabled = !!this.brrSettings.translations_attributes?.find(
        (translation) => translation.locale === this.$i18n.locale && translation.locale_enabled
      )?.title
      return (
        !window.__tripla_guide_website__ &&
        this.brrSettings.is_active &&
        this.isBrrWidgetDisplay &&
        this.brrPriceList.length > 0 &&
        localeEnabled
      )
    },

    hasChatChannel() {
      const hasMultipleChatChannels = this.settingsStore.availableChatChannels.length > 1
      return hasMultipleChatChannels ? !!this.appStore.chatChannel : true
    },

    botIconBottom() {
      if (isMobile() && this.isRenderedBrr) {
        return this.botIconStore.bottom + mobileBrrOffset
      }
      return this.botIconStore.bottom
    },

    botIconRight() {
      return this.botIconStore.right
    },

    isMinimized() {
      return this.appStore.isMinimized
    },

    userId() {
      return this.userStore.id
    },

    isChatOpen: {
      get() {
        return this.appStore.isChatWindowOpened
      },

      set(value) {
        if (value && !this.shouldLoadIframeResource) {
          this.shouldLoadIframeResource = true
        }
        this.appStore.setChatWindowOpened(value)
      }
    },

    minimizedChatIconStyle() {
      const common = {
        position: 'fixed',
        bottom: 0,
        right: 0,
        cursor: 'pointer',
        background: this.settings.minimized_icon.color,
        color: this.settings.minimized_icon.text_color
      }

      if (this.$i18n.locale === 'en') {
        return {
          ...common,
          writingMode: 'vertical-lr',
          textOrientation: 'sideways',
          transform: 'rotate(180deg)',
          padding: '8px 0',
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px'
        }
      } else {
        return {
          ...common,
          writingMode: 'tb',
          padding: '8px 0',
          borderTopLeftRadius: '4px',
          borderBottomLeftRadius: '4px'
        }
      }
    },

    chatMaximumIconStyle() {
      const iconStyle = {
        position: 'absolute',
        borderRadius: '50%',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        background: '#fff',
        width: '20px',
        height: '20px',
        textAlign: 'center'
      }
      if (this.$i18n.locale === 'en') {
        return {
          ...iconStyle,
          bottom: '-10px',
          right: '-12px'
        }
      } else {
        return {
          ...iconStyle,
          top: '-10px',
          left: '-12px'
        }
      }
    },

    chatFrameStyle() {
      if (this.appStore.isFullScreen) {
        return {
          ...chatFrameStyle,
          zIndex: Number(this.triplabotZIndexPosition) - 1
        }
      }

      return {
        position: 'absolute',
        border: 'none',
        width: '370px',
        zIndex: Number(this.triplabotZIndexPosition) - 1,
        overflow: 'hidden',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
        borderRadius: '10px',
        bottom: '10px',
        right: `${this.settingsStore.triplabotWindowRight}px`,
        height: '650px',
        maxHeight: 'unset !important',
        margin: 'unset !important',
        maxWidth: 'unset !important',
        ...(this.maximizedChat
          ? {
              width: '740px',
              height: 'calc(100vh - 30px)'
            }
          : {})
      }
    },

    termsConditionsIframeStyle() {
      return {
        border: 0,
        position: 'fixed',
        top: 0,
        left: this.termsConditionsStore.show ? 0 : '-100vw',
        width: '100vw',
        height: '100%', // should not use 100vh here https://dev.to/maciejtrzcinski/100vh-problem-with-ios-safari-3ge9
        maxHeight: 'unset !important', // avoid facilities override max-height, we know this is weird
        margin: 'unset !important',
        zIndex: 2147483647
      }
    },

    isChatbotActive() {
      const urlQuery = window.location.search.substring(1)
      if (/triplabot_hide=true/.test(urlQuery)) {
        return false
      }
      return this.settings.active || this.isCustomBotTestMode
    },

    divWrapStyleComputed: function () {
      if (this.isChatOpen) {
        if (this.appStore.isFullScreen) {
          this.divWrapStyle.width = '100%'
          this.divWrapStyle.height = `${window.innerHeight}px`
          this.divWrapStyle.zIndex = '2147483647'
        } else {
          this.divWrapStyle = divWrapOpenPC
          this.divWrapStyle.zIndex = '2147483647'
        }
      } else {
        if (this.appStore.isFullScreen) {
          this.divWrapStyle = divWrap
          this.divWrapStyle.width = '0'
          this.divWrapStyle.height = '0'
          this.divWrapStyle.zIndex = this.triplabotZIndexPosition
        } else {
          this.divWrapStyle = divWrap
          this.divWrapStyle.zIndex = this.triplabotZIndexPosition
        }
      }

      this.divWrapStyle.zIndex += ' !important'

      return {
        ...this.divWrapStyle,
        whiteSpace: 'normal' // avoid inherit another value from parents
      }
    },
    getIframeHeight() {
      return this.isMinimized ? 0 : this.iconMessageStore.getIframeHeight
    },
    getIconSizeOffset() {
      // 30% of icon size as design
      // and minus by 10px because icon message iframe has padding of 10px
      return Math.round(this.iconSize * 0.3) - 10
    },
    isExperimentalEnabled() {
      return !!this.settings.new_icon_message
    },
    getIconMessageIframeBottomPosition() {
      const botIconBottom = this.botIconStore.bottom
      const iconSize = this.appStore.isFullScreen
        ? this.botIconStore.iconSizeMobile
        : this.botIconStore.iconSizePC
      let result = this.isExperimentalEnabled
        ? botIconBottom + this.getIconSizeOffset
        : this.iconMessageStore.position === 'left'
        ? botIconBottom
        : botIconBottom + iconSize

      if (isMobile() && this.isRenderedBrr) {
        result += mobileBrrOffset
      }

      return `${result}px`
    },
    iconMessageIframeBottomBrrPosition() {
      if (this.brrPosition === 'left') {
        return `20px`
      } else {
        const iconSize =
          this.botIconBottom +
          (this.appStore.isFullScreen
            ? this.botIconStore.iconSizeMobile
            : this.botIconStore.iconSizePC)
        const result =
          this.iconMessageStore.position === 'left'
            ? this.iconMessageStore.height + this.botIconBottom + 10
            : this.iconMessageStore.height + iconSize + 10
        return `${result}px`
      }
    },
    getIconMessageIframeRightPositionValue() {
      const botIconRight = this.botIconStore.right
      const iconSize = this.appStore.isFullScreen
        ? this.botIconStore.iconSizeMobile
        : this.botIconStore.iconSizePC
      const result = this.isExperimentalEnabled
        ? botIconRight + this.getIconSizeOffset
        : this.iconMessageStore.position === 'left'
        ? botIconRight + iconSize
        : botIconRight
      return result
    },
    iconMessageIframeRightBrrPosition() {
      return this.brrPosition === 'left'
        ? ''
        : `${this.getIconMessageIframeRightPositionValue + 10}px`
    },
    iconMessageIframeLeftBrrPosition() {
      return this.brrPosition === 'right' ? '' : `${this.getIconMessageIframeRightPositionValue}px`
    },
    iconSize() {
      return this.botIconStore.iconSize + 'px'
    },
    iconMessageIsShown() {
      const iconMessages = this.iconMessageStore.getIconMessages
      return iconMessages.length > 0 && !this.isChatOpen
    }
  },

  watch: {
    isChatOpen: {
      handler(isOpen) {
        if (isOpen && this.chatOpenCount === 0) {
          this.syncBWSignInState()
        }

        if (isOpen) {
          this.chatOpenCount += 1
          this.checkLocaleAndUpdate()
        }
      },
      immediate: true
    },
    currentHref() {
      this.getPositionRule()
    }
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handleMessageFromBW)
    window.removeEventListener('hashchange', this.getPositionRule)
    window.removeEventListener('resize', this.debouncedUpdateIconMessageIframe)
    if (this.settingsStore.settings.booking_widget_is_active) {
      window.removeEventListener('focus', this.checkLocaleAndUpdate)
    }
  },

  created() {
    const ministopBrandIds = (process.env.MINISTOP_BRAND_IDS || '').split(',')
    if (ministopBrandIds.includes(this.settings?.hotel_brand_id?.toString())) {
      this.shouldLoadIframeResource = true
    }
    window.addEventListener('message', this.handleMessageFromBW)
    window.addEventListener('hashchange', () => {
      this.currentHref = window.location.href
    })
    if (!this.isChatbotActive) return
    createAndAppendStyle()

    // get all data-triplabot-toggle and add event on click to toggleChat
    this.getDataTriplabotToggle()
    this.getDataOpenAutoMessage()
    this.getDataTriplabotRedirect()

    setDefaultIconPosition(this.appStore.isFullScreen)

    // This event triggers when changing the language from ./ChatWindow/Chats/LanguageWidget.vue
    EventBus.$on('change-locale', (locale) => {
      const chatWindowFrameDoc =
        this.$refs.chatWindowIframe.contentDocument ||
        this.$refs.chatWindowIframe.contentWindow.document
      const languageMap = {
        // this is evil, it should not exist
        en: 'en',
        ja: 'ja',
        ko: 'ko',
        zh_Hans: 'zh-Hans', // this is the only reason we need the map hyphens vs underscores
        zh_Hant: 'zh-Hant'
      }
      chatWindowFrameDoc.documentElement.lang = languageMap[locale]
    })

    EventBus.$on('toggle-chat', () => {
      this.toggleChat()
    })

    EventBus.$on('maximize-chat', (value) => {
      this.maximizedChat = value
      if (!value) {
        this.adjustIframeChatWindow()
      }
    })

    EventBus.$on('service-unavailable', () => {
      this.$maintenanceStore.updateMode(true)
    })

    window.triplaToggleChat = this.toggleChat
  },
  mounted() {
    if (this.isChatbotActive) {
      this.attachIconEvents()
      this.setImageInitialSettings()
    }
    if (this.settingsStore.settings.booking_widget_is_active) {
      window.addEventListener('focus', this.checkLocaleAndUpdate)
    }
    this.processTermsConditionsFlow()
    this.animateChatIcon()
    window.addEventListener('resize', this.debouncedUpdateIconMessageIframe)

    const disabledBrrWidgetBrandIds = (process.env.DISABLED_BRR_WIDGET_BRAND_IDS || '').split(',')
    const isBRRWidgetEnabled = !disabledBrrWidgetBrandIds.includes(this.settings.hotel_brand_id?.toString())
    if (isBRRWidgetEnabled) {
      this.getBrrSetting()
    }

    if (!this.shouldLoadIframeResource) {
      window.addEventListener('scroll', this.initRenderChatWindow, { once: true, passive: true })
      window.addEventListener('mousemove', this.initRenderChatWindow, { once: true, passive: true })
      window.addEventListener('touchstart', this.initRenderChatWindow, {
        once: true,
        passive: true
      })
      window.addEventListener('keydown', this.initRenderChatWindow, { once: true, passive: true })
    }
  },
  methods: {
    initRenderChatWindow() {
      if (!this.shouldLoadIframeResource) {
        window.removeEventListener('scroll', this.initRenderChatWindow)
        window.removeEventListener('mousemove', this.initRenderChatWindow)
        window.removeEventListener('touchstart', this.initRenderChatWindow)
        window.removeEventListener('keydown', this.initRenderChatWindow)
      }
      this.shouldLoadIframeResource = true
    },
    setImageInitialSettings() {
      const initialImage = getChatIconImageFromSettings(this.settingsStore.settings)
      this.appStore.setImageURL(initialImage)
    },
    getBrrPriceSearchParams() {
      const { checkin, checkout, adults, is_day_use, type, rooms } = this.newSearchParams
      const roomsPayload = [
        {
          checkin_date: checkin,
          checkout_date: checkout,
          adults: rooms?.length > 0 ? rooms[0].adults : adults,
          children: 0
        }
      ]

      const payload = {
        rooms: roomsPayload,
        order: 'price_low_to_high',
        is_day_use,
        is_including_occupied: false,
        bookable: true,
        type
      }

      this.adultsForLinkAndDisplay = roomsPayload[0].adults

      this.priceSearchParams = {
        checkIn: checkin,
        checkOut: checkout,
        adults: this.adultsForLinkAndDisplay,
        children: 0
      }

      return payload
    },
    hasBrrPrices(item) {
      const isBrrExist = item.non_sign_in_discount?.brr_prices?.some(
        (brr) => brr.ota_price_value > 0
      )
      if (!isBrrExist) return false
      const brrPrices = item.non_sign_in_discount?.brr_prices
        ?.map((brr) => brr.ota_price_value)
        .filter((price) => price)
      const signInDiscount = item.sign_in_discount
      const directPrice =
        signInDiscount?.total_price_after_discount +
        signInDiscount?.total_price_after_discount_tax +
        signInDiscount?.total_accommodation_tax
      const isCheapest = Math.min(...brrPrices) > directPrice
      return isCheapest
    },
    async getBrrPriceList() {
      if (
        this.newSearchParams.kidsTiers?.filter((tier) => tier.children > 0).length > 0 ||
        this.newSearchParams.rooms?.[0]?.children > 0 ||
        !this.brrSettings.is_active
      ) {
        return
      }
      this.otaPriceFetching = true
      const searchParams = this.getBrrPriceSearchParams()
      try {
        const response = await BrrWidgetApi.getOtaPrice(this.settingsStore.getHotelId, searchParams)
        const isPlanType = this.newSearchParams.type === 'plan'
        let targetItem
        if (isPlanType) {
          const plan = response.plans.find((plan) => plan.rooms.some(this.hasBrrPrices))
          targetItem = plan?.rooms.find(this.hasBrrPrices)
        } else {
          const room = response.rooms.find((room) => room.room_plan.some(this.hasBrrPrices))
          targetItem = room?.room_plan.find(this.hasBrrPrices)
        }

        if (targetItem) {
          this.brrPriceList = targetItem.non_sign_in_discount?.brr_prices.filter(
            (price) => price.ota_price_value > 0
          )
          this.targetRoomPlan = targetItem
        } else {
          this.brrPriceList = []
          this.targetRoomPlan = null
        }
      } catch (error) {
        this.brrPriceList = []
        this.targetRoomPlan = null
        console.error(error)
      } finally {
        this.otaPriceFetching = false
      }
    },
    async getBrrSetting() {
      try {
        const urlObj = new URLSearchParams(location.search)
        const previewKey = urlObj.get('preview_brr_key')
        const brrResponse = await (previewKey
          ? TemporaryStorageApi.getTemporaryStorage(previewKey)
          : BrrWidgetApi.getBrrWidgetSetting(this.appStore.websiteFacilityCode))
        this.brrSettings = brrResponse.data
        this.reservationFormStore.setDefaultSearchSetting(
          this.settingsStore.getSettings.default_search_setting
        )
        const urlParams = new URL(window.location.href).searchParams
        const checkin = urlParams.get('checkin')
        if (!checkin) {
          this.newSearchParams.adults = this.reservationFormStore.adults
          this.newSearchParams.checkin = this.reservationFormStore.startDate
          this.newSearchParams.checkout = this.reservationFormStore.endDate
        }
        this.getPositionRule()
        await this.getBrrPriceList()
        if (this.brrPriceList.length === 0) {
          const defaultCheckinTime = new Date(this.newSearchParams.checkin).getTime()
          this.newSearchParams.checkin = formatDate(
            new Date(defaultCheckinTime + 30 * 24 * 60 * 60 * 1000)
          )
          this.newSearchParams.checkout = formatDate(
            new Date(defaultCheckinTime + 31 * 24 * 60 * 60 * 1000)
          )
          this.getBrrPriceList()
        }
      } catch (err) {
        console.error(err)
      }
    },
    handleOpenBw() {
      const searchPayload = {
        ...this.createSearchParams(this.getBrrPriceSearchParams()),
        utm_source: 'tripla_bot',
        utm_medium: 'brr_widget',
        is_including_occupied: false
      }
      if (this.chatStore.chatId) {
        searchPayload.utm_campaign = this.chatStore.chatId
      }
      const bwIframe = document.getElementById('tripla-booking-widget-window')
      if (bwIframe || this.settingsStore.isBWOpenInNewTab) {
        const bwURL = new URL(this.settingsStore.bookingWidgetURL)
        const params = new URLSearchParams()

        for (const [key, value] of Object.entries(searchPayload)) {
          if (typeof value === 'object') {
            params.append(key, JSON.stringify(value))
          } else {
            params.append(key, value)
          }
        }
        const bwPage = `${bwURL.origin}/booking/result?${params.toString()}&parentUrl=${
          location.origin
        }`
        if (this.settingsStore.isBWOpenInNewTab) {
          window.open(bwPage, '_blank')
        } else {
          bwIframe.src = bwPage
        }
      } else {
        openBookingWidget(searchPayload)
      }
    },
    createSearchParams(searchParams) {
      const { rooms, type } = searchParams
      const checkin = rooms[0].checkin_date
      const checkout = rooms[0].checkout_date
      const roomPlanCode = this.targetRoomPlan.room_plan_code

      return {
        code: this.appStore.websiteFacilityCode,
        type,
        checkin,
        checkout,
        adults: this.adultsForLinkAndDisplay,
        room_plan_code: roomPlanCode
      }
    },
    getPositionRule() {
      if (Object.keys(this.brrSettings).length === 0) {
        this.brrSettings.is_active = false
        return
      }
      // https://app.rollbar.com/a/kaku-2/fix/item/FE-chatbot/11703
      if (this.brrSettings.position_settings?.type === 'all_pages') {
        const position = this.brrSettings.position_settings.position
        this.brrPosition = position.includes('left') ? 'left' : 'right'
      } else {
        this.evaluatePositionRules()
      }
    },
    evaluatePositionRules() {
      if (!this.brrSettings || !this.brrSettings.position_settings) return
      let currentUrl = new URL(window.location.href)
      if (currentUrl.searchParams.has('preview_key')) {
        currentUrl.searchParams.delete('preview_key')
      }
      currentUrl = currentUrl.toString()
      const { rules, type } = this.brrSettings.position_settings

      if (type === 'following_pages' && this.brrSettings.is_active) {
        const matchedPage = findMatchedPage(rules)
        this.brrSettings.is_active = !!matchedPage
        this.brrPosition = matchedPage?.position.includes('left') ? 'left' : 'right'
      }
    },
    checkLocaleAndUpdate() {
      if (!this.isChatOpen) return
      this.openBwToGetLocale()
    },
    openBwToGetLocale: _debounce(function () {
      const bwURL = new URL(this.settingsStore.bookingWidgetURL)
      const iframe = document.createElement('iframe')
      iframe.src = `${bwURL.origin}/iframe-change-locale?parentUrl=${location.origin}`
      iframe.style.display = 'none'
      document.body.appendChild(iframe)
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 3000)
    }, 3000),
    syncBWSignInState() {
      const url = new Url(window.location, '/', true)
      const isNotConfirmingEmail = !url.query.triplabot_confirm_user
      const isNotSyncedWithBWForFirstTime =
        !triplaLocalStorage.getItem('synced_bw_sign_in_state') &&
        !this.settingsStore.isOnStandaloneBookingWidget
      const shouldSyncViaLoginToken =
        !!this.scriptConfig.dataAttrs['data-triplabot-login-token'] && !this.userStore.isKnownUser
      const shouldOpenBWAndSyncSignInState =
        this.isFacilitySignInEnabled &&
        (isNotSyncedWithBWForFirstTime || shouldSyncViaLoginToken) &&
        isNotConfirmingEmail &&
        !this.isCustomBotTestMode
      const shouldOpenBWAndSyncSignInStateNewSignInEnabled =
        this.isFacilitySignInEnabled &&
        triplaLocalStorage.getItem('triplabot_auth') === 'triplabot_sign_in' &&
        !this.isCustomBotTestMode

      if (shouldOpenBWAndSyncSignInState || shouldOpenBWAndSyncSignInStateNewSignInEnabled) {
        this.showBWIframe = true
      }
    },

    minimize(value = true) {
      this.appStore.setMinimized(value)
    },

    onChatIconDragStart() {
      if (!this.appStore.isFullScreen) {
        document.addEventListener('dragover', this.preventDrag, false)
        document.addEventListener('drop', this.iconDrop, false)
      }
    },

    onChatIconDragEnd() {
      document.removeEventListener('dragover', this.preventDrag, false)
      document.removeEventListener('drop', this.iconDrop, false)
    },

    animateChatIcon() {
      if (!this.chatIconAnimationSettings.type) return

      const animate = () => {
        if (!this.isAnimationCancelled) {
          this.animateCSS(this.$refs['chatIcon'].$el, this.chatIconAnimationSettings.type).then(
            () => {
              this.chatIconAnimationTimerId = setTimeout(
                animate,
                this.chatIconAnimationSettings.duration * 1000
              )
            }
          )
        }
      }

      animate()

      if (this.chatIconAnimationSettings.stop_animation === 'after_seconds') {
        setTimeout(
          this.cancelChatIconAnimation,
          this.chatIconAnimationSettings.stop_after_seconds * 1000
        )
      }
    },

    cancelChatIconAnimation() {
      this.isAnimationCancelled = true
      clearTimeout(this.chatIconAnimationTimerId)
      this.chatIconAnimationTimerId = undefined
      this.removeAnimation(this.$refs['chatIcon'].$el, this.chatIconAnimationSettings.type)
    },

    removeAnimation(element, animation, prefix = 'animate__') {
      const animationName = `${prefix}${animation}`
      element.classList.remove(`${prefix}animated`, animationName)
    },

    animateCSS(element, animation, prefix = 'animate__') {
      return new Promise((resolve) => {
        if (!element) resolve()
        const animationName = `${prefix}${animation}`

        element.classList.add(`${prefix}animated`, animationName)

        element.addEventListener(
          'animationend',
          (event) => {
            event.stopPropagation()
            this.removeAnimation(element, animation)
            resolve()
          },
          { once: true }
        )
      })
    },

    async toggleChat() {
      if (!this.shouldLoadIframeResource) {
        this.$rollbar.info(`showIframe is false, but user triggers toggleChat`)
        this.shouldLoadIframeResource = true
        await delay(1000)
      }
      // toggles the chat and changing all the styles accordingly
      if (
        !this.isChatOpen &&
        !this.isCustomBotTestMode &&
        !this.termsConditionsStore.accepted &&
        this.settings.terms_and_conditions_enabled
      ) {
        this.termsConditionsStore.setShow(true)
        this.$gtag.recordEventUserPolicy('Show')
        return
      }

      const selectedChatChannel = this.appStore.chatChannel
      if (
        !this.isChatOpen &&
        selectedChatChannel &&
        [ChatChannels.MESSENGER, ChatChannels.LINE, ChatChannels.WHATSAPP].includes(
          selectedChatChannel
        )
      ) {
        this.appStore.openChatChannel(selectedChatChannel)
        return
      }

      this.isChatOpen = !this.isChatOpen

      const htmlElement = document.documentElement
      if (this.isChatOpen) {
        if (
          this.chatIconAnimationSettings.stop_animation === 'chatbot_open' &&
          (this.chatIconAnimationTimerId || !this.isAnimationCancelled)
        ) {
          this.cancelChatIconAnimation()
        }

        if (!this.appStore.isFullScreen) {
          this.adjustIframeChatWindow()
          EventBus.$emit('focus-text-message')
        } else {
          htmlElement.classList.add(htmlTriplaClassName)
        }
        this.chatStore.triggerRestaurantCode()
      } else {
        if (this.appStore.isFullScreen) {
          htmlElement.classList.remove(htmlTriplaClassName)
        }
      }
      EventBus.$emit('update-chat-iframe')
    },
    iconDragStart(e) {
      e.dataTransfer.setData('text', 'triplabot-icon')
      this.dragPosition = {
        x: e.screenX,
        y: e.screenY
      }
    },
    preventDrag(e) {
      e.preventDefault()
    },
    iconDrop(e) {
      if (e.dataTransfer.getData('text') === 'triplabot-icon') {
        e.preventDefault()
        this.iconFramePositionBottom -= e.screenY - this.dragPosition.y
        this.iconFramePositionRight -= e.screenX - this.dragPosition.x
        this.botIconStore.setBotIconBottom(this.iconFramePositionBottom)
        this.botIconStore.setBotIconRight(this.iconFramePositionRight)
      }
    },
    iconTouchStart(e) {
      this.touchStartPosition = e.targetTouches[0]
      this.currentTouch = e.targetTouches[0]
    },
    iconTouchMove(e) {
      e.preventDefault()
      this.currentTouch = e.targetTouches[0]
      const bottom =
        this.iconFramePositionBottom -
        touchHeight(this.currentTouch.screenY - this.touchStartPosition.screenY)
      const right =
        this.iconFramePositionRight -
        touchWidth(this.currentTouch.screenX - this.touchStartPosition.screenX)
      this.botIconStore.setBotIconBottom(bottom)
      this.botIconStore.setBotIconRight(right)
    },
    iconTouchEnd(e) {
      if (this.currentTouch) {
        this.iconFramePositionBottom -= touchHeight(
          this.currentTouch.screenY - this.touchStartPosition.screenY
        )
        this.iconFramePositionRight -= touchWidth(
          this.currentTouch.screenX - this.touchStartPosition.screenX
        )
      }
    },

    setIconFrame(dataRecievedMobile, dataRecievedPC, defaultValueMobile, defaultValuePC) {
      if (this.appStore.isFullScreen) {
        return dataRecievedMobile || defaultValueMobile
      } else {
        return dataRecievedPC || defaultValuePC
      }
    },
    loadTermsConditionsModalComponent(e) {
      const termsConditionsFrameDoc = e.target.contentDocument || e.target.contentWindow.document
      buildIframePage(
        e.target,
        'terms-conditions-modal',
        triplaLocalStorage.getItem('triplabot_locale'),
        undefined,
        this.appStore.websiteFacilityCode
      )

      const vueApp = new Vue({
        pinia: this.$pinia,
        i18n: this.$i18n,
        components: { TermsConditionsModal },
        methods: {
          toggleChat: this.toggleChat
        },
        render(h) {
          return h(TermsConditionsModal, {
            on: {
              agree: this.toggleChat
            }
          })
        }
      })
      termsConditionsFrameDoc.body.style.background = 'transparent' // override global styles
      vueApp.$mount(termsConditionsFrameDoc.getElementById('terms-conditions-modal'))
    },

    updateIconMessageIframe() {
      const iframe = this.$refs['icon-message-iframe']
      if (!iframe) return

      const rect = iframe.getBoundingClientRect()
      this.iconMessageStore.updateIframeMaxWidth(Math.floor(rect.right))
      this.iconMessageStore.updateIframeMaxWidthLeft(Math.floor(rect.right))
      this.iconMessageStore.updateIframeMaxHeight(Math.floor(rect.bottom))
    },

    debouncedUpdateIconMessageIframe: debounce(function () {
      this.iconMessageIframeTransitionEnabled = true
      this.updateIconMessageIframe()
    }, 250),

    loadIconMessageComponent(e) {
      this.updateIconMessageIframe()

      const iconMessageFrameDoc = e.target.contentDocument || e.target.contentWindow.document
      buildIframePage(
        e.target,
        'iconmessagewindow-app',
        triplaLocalStorage.getItem('triplabot_locale'),
        'icon-message',
        this.appStore.websiteFacilityCode
      )
      const vueApp = new Vue({
        name: 'IconMessageIframe',
        pinia: this.$pinia,
        i18n: this.$i18n,
        data: {
          toggleChat: this.toggleChat
        },
        components: { IconMessageWindow },
        render(h) {
          return h(IconMessageWindow, {
            props: {
              toggleChat: this.toggleChat
            }
          })
        }
      })
      vueApp.$mount(iconMessageFrameDoc.getElementById('iconmessagewindow-app'))
    },
    loadChatWindowComponent(e) {
      const chatWindowFrameDoc = e.target.contentDocument || e.target.contentWindow.document
      buildIframePage(
        e.target,
        'chatwindow-app',
        triplaLocalStorage.getItem('triplabot_locale'),
        undefined,
        this.appStore.websiteFacilityCode
      )

      const vueApp = new Vue({
        name: 'ChatWindow',
        i18n: this.$i18n,
        pinia: this.$pinia,
        components: { ChatWindow },
        data: {
          chatWindowProps: this.chatWindowProps,
          code: this.appStore.websiteFacilityCode,
          isFullScreen: this.appStore.isFullScreen,
          toggleChat: this.toggleChat,
          widgetInitial: true,
          openChatByDefault: this.openChatByDefault
        },
        created() {
          EventBus.$on('update-chat-iframe', () => {
            this.widgetInitial = false
          })
          this.openChatByDefault()
        },
        render(h) {
          return h(ChatWindow, {
            props: {
              toggleChat: this.toggleChat,
              widgetInitial: this.widgetInitial
            }
          })
        }
      })

      vueApp.$mount(chatWindowFrameDoc.getElementById('chatwindow-app'))
    },

    adjustIframeChatWindow() {
      const windowHeight = window.innerHeight - 100 // 100 comes from the bottom 80px plus iframe space 20px
      let adjustedWindowHeight = windowHeight
      if (windowHeight > maximumChatWindowHeight) {
        adjustedWindowHeight = maximumChatWindowHeight
      } else if (windowHeight < minimumChatWindowHeight) {
        adjustedWindowHeight = minimumChatWindowHeight
      }
      this.chatFrameStyle.height = `${adjustedWindowHeight}px`
    },
    setDefaultBrowserLanguage(browserLanguage) {
      if (localeMapping.hasOwnProperty(browserLanguage)) {
        return localeMapping[browserLanguage]
      } else {
        return 'en'
      }
    },
    getDataOpenAutoMessage() {
      const openAutoMessageElements = document.querySelectorAll('[data-triplabot-open-message]')

      Array.from(openAutoMessageElements).forEach((element) => {
        element.addEventListener(
          'click',
          (e) => {
            e.preventDefault()
            this.toggleChat()
            const openMessageValue = element.getAttribute('data-triplabot-open-message')
            if (this.isChatOpen === true) {
              this.chatStore.showAutoMessageOpen(
                this.settings.open_triplabot_message,
                openMessageValue
              )
              this.$gtag.recordEventChatOpenByDataTriplabotOpenMessage(element)
            }
          },
          false
        )
      })
    },
    getDataTriplabotToggle() {
      const toggleElements = document.querySelectorAll('[data-triplabot-toggle="1"]')

      Array.from(toggleElements).forEach((element) => {
        element.addEventListener(
          'click',
          (e) => {
            e.preventDefault()
            this.toggleChat()
            if (this.isChatOpen === true) {
              this.chatStore.showAutoMessageOpen(this.settings.open_triplabot_message, null)
              this.$gtag.recordEventChatOpenByDataTriplabotToggle(element)
            }
          },
          false
        )
      })
    },
    getDataTriplabotRedirect() {
      const redirectElements = document.querySelectorAll('[data-triplabot-redirect-open]')

      Array.from(redirectElements).forEach((element) => {
        element.addEventListener('click', (e) => {
          e.preventDefault()
          const redirectURL = element.getAttribute('data-triplabot-redirect-open')
          window.location.replace(redirectURL)
        })
      })
    },
    openChatByDefault() {
      if (!this.isChatbotActive) return

      const urlQuery = window.location.search.substring(1)
      if (
        /triplabot_open=true/.test(urlQuery) ||
        isWebView ||
        /triplabot_confirm_user=/.test(urlQuery)
      )
        this.toggleChat()
    },
    attachIconEvents() {
      const iconElement = this.$refs.iconElement
      if (!iconElement) return

      if (this.appStore.isFullScreen) {
        iconElement.addEventListener('touchstart', this.iconTouchStart, false)
        iconElement.addEventListener('touchmove', this.iconTouchMove, false)
        iconElement.addEventListener('touchend', this.iconTouchEnd, false)
      } else {
        if (this.settingsStore.isChatIconDraggable) {
          iconElement.addEventListener('dragstart', this.iconDragStart, false)
        }
      }
    },

    processTermsConditionsFlow() {
      if (!this.settingsStore.isTermsConditionsEnabled) return

      User.GetTermsConditionsStatus(this.settingsStore.getHotelId)
        .then((res) => {
          this.termsConditionsStore.setAccepted(res.data.accept_terms_and_conditions)
          return res.data.accept_terms_and_conditions || false
        })
        .then((isAccepted) => {
          if (isAccepted) return

          Setting.getTermsConditions(this.appStore.websiteFacilityCode).then((tcResponse) => {
            this.termsConditionsStore.setSettings(tcResponse.data)
          })
        })
    },

    handleMessageFromBW(event) {
      const bwURL = new URL(this.settingsStore.bookingWidgetURL)
      if (event.origin !== bwURL.origin) return

      const { data } = event

      if (!this.isChatbotActive) return
      const isKnownUser = triplaLocalStorage.getItem('triplabot_known_user_signed_in') === 'true'
      const cookieDomain = getDomainWithoutSubdomain(window.location.toString())
      if (data.action === 'receiveTriplabotSession' && data.session) {
        // user has signed in on BW
        this.minimize(false)
        storeSession(data.session)
        this.chatStore.refreshChat()
        if (data.isInCBIframe) {
          userSync.refreshBWLogin()
        }
        triplaLocalStorage.setItem('synced_bw_sign_in_state', true)
        this.chatStore.changeChat(triplaLocalStorage.getItem('original_brand_hotel_code'))
      } else if (data.action === 'login' && !data.name && isKnownUser) {
        // user has signed out on BW
        this.minimize(false)
        removeSession()

        if (data.isInCBIframe) {
          userSync.refreshBWLogin()
        }
        if (!data.skipChatbotRefresh) {
          this.chatStore.refreshChat()
        }
      } else if (data.action === 'getTriplabotUserId') {
        event.source.postMessage(
          { action: 'getTriplabotUserId', id: triplaLocalStorage.getItem('triplabot_user_id') },
          event.origin
        )
      } else if (data.action === 'triplabotSynced') {
        triplaLocalStorage.setItem('synced_bw_sign_in_state', true)
        this.showBWIframe = false
      } else if (data.action === 'made_reservation' && data.segment_ids) {
        const metrics = data.segment_ids.map((id) => ({
          action: 'made_reservation',
          target_type: 'icon_action',
          target_id: data.icon_message_id,
          segment_id: id,
          user_id: this.userId
        }))
        Cdp.createMetrics(metrics)
      } else if (data.action === 'show-chatbot') {
        if (this.isChatOpen) {
          this.toggleChat()
        }
      } else if (data.action === 'hide-chatbot-sign-in') {
        this.appStore.setHideSignIn(data.value)
        userSync.setState({ hideSignIn: data.value })
      } else if (data.action === 'send-search-params') {
        this.newSearchParams = data.payload
        this.getBrrPriceList()
      } else if (data.action === 'close-brr-widget') {
        this.isBrrWidgetDisplay = false
      } else if (data.action === 'close') {
        this.appStore.setHideSignIn(false)

        if (this.isCustomBotTestMode) {
          this.isChatOpen = true
        }
      } else if (data.action === 'bwOrganizationSignedIn') {
        Cookies.set(CookieKeys.BW_ORG_SIGNED_IN, true, {
          Domain: cookieDomain,
          'Max-Age': '315360000', // 1 year
          'Same-Site': 'Strict'
        })
      } else if (data.action === 'bw-change-locale' && data.locale !== this.$i18n.locale) {
        if (!this.settingsStore.getSettings.bot_languages.includes(data.locale)) {
          return
        }
        this.$i18n.locale = data.locale
        triplaLocalStorage.setItem('triplabot_locale', data.locale)
        EventBus.$emit('change-locale', data.locale)
        if (typeof window.triplaSearchBarChangeLocale === 'function') {
          window.triplaSearchBarChangeLocale(data.locale)
        }
      }

      if (data.action === 'login' && !data.name && data.currentOrgId) {
        Cookies.remove(CookieKeys.BW_ORG_SIGNED_IN, { domain: cookieDomain })
      }
    }
  }
}
</script>

<style>
@import '~scss/animate.scss';
</style>

<style lang="scss" scoped>
.tripla-scale-enter-active,
.tripla-scale-leave-active {
  transition: all 0.25s ease-in-out;
  transform: scale(1);
  opacity: 1;
}

.tripla-scale-enter,
.tripla-scale-leave-to {
  transform: scale(0);
  opacity: 0;
}

#tripla-chat-window {
  transform: scale(0);
  opacity: 0;
  transition: all 0.25s ease-in-out;
  transform-origin: bottom right;

  &.tripla-chat-window--show {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
